import { useClipboard } from '@pesto/ui';
import { Badge } from '@pesto/ui/components/Badge';
import { Button } from '@pesto/ui/components/Button';
import { Typography } from '@pesto/ui/components/Typography';
import { cn } from '@pesto/utils';
import { formatDate } from '@pesto/utils';
import { Copy } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { EntityField, EntityFieldGridConfig } from '../types/EntityFieldGrid.types';

interface EntityFieldGridProps {
  config: EntityFieldGridConfig;
  className?: string;
}

const DASH = '—';

const formatCurrency = (cents: number): string => {
  const dollars = cents / 100;

  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dollars);
};

const renderFieldValue = (field: EntityField, copy: (value: string) => void) => {
  switch (field.kind) {
    case 'text': {
      const value = field.value;
      const display = value === null || value === undefined || value === '' ? DASH : String(value);

      return (
        <Typography variant="body" className="font-medium">
          {display}
        </Typography>
      );
    }

    case 'date': {
      const formatted = field.value ? formatDate(field.value, field.format) : '';

      return (
        <Typography variant="body" className="font-medium">
          {formatted || DASH}
        </Typography>
      );
    }

    case 'currency': {
      const display = field.value === null || field.value === undefined ? DASH : formatCurrency(field.value);

      return (
        <Typography variant="body" className="font-medium">
          {display}
        </Typography>
      );
    }

    case 'badge': {
      if (!field.value) {
        return (
          <Typography variant="body" className="text-muted-foreground">
            {DASH}
          </Typography>
        );
      }

      return (
        <Badge variant={field.variant ?? 'outline'} className="w-fit text-xs">
          {field.value}
        </Badge>
      );
    }

    case 'link': {
      if (field.external) {
        return (
          <a href={field.href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            <Typography variant="body" className="font-medium">
              {field.value}
            </Typography>
          </a>
        );
      }

      return (
        <Link to={field.href} className="text-primary hover:underline">
          <Typography variant="body" className="font-medium">
            {field.value}
          </Typography>
        </Link>
      );
    }

    case 'copyable': {
      if (!field.value) {
        return (
          <Typography variant="body" className="text-muted-foreground">
            {DASH}
          </Typography>
        );
      }

      return (
        <div className="flex items-center gap-2">
          <Typography variant="body" className={cn('font-medium', field.mono && 'font-mono text-sm')}>
            {field.value}
          </Typography>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 p-1"
            onClick={() => copy(field.value ?? '')}
            aria-label={`Copy ${field.label}`}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      );
    }

    case 'custom':
      return field.render();
    default:
      return null;
  }
};

const COLUMN_CLASS: Record<NonNullable<EntityFieldGridConfig['columns']>, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
};

export const EntityFieldGrid = ({ config, className }: EntityFieldGridProps) => {
  const { fields, columns = 2 } = config;
  const { copy } = useClipboard();

  return (
    <dl className={cn('grid gap-4', COLUMN_CLASS[columns], className)}>
      {fields.map((field, idx) => (
        <div key={`${field.label}-${idx}`} className="space-y-1">
          <dt>
            <Typography variant="caption" className="text-muted-foreground uppercase tracking-wide">
              {field.label}
            </Typography>
          </dt>
          <dd>{renderFieldValue(field, (value) => void copy(value))}</dd>
        </div>
      ))}
    </dl>
  );
};
